import React from "react";
import AIChatbotSection from "@components/AIChatbotSection";
import AiChatbotSectionMobile from "@components/AiChatbotSections";
import AiCompareComponent from "@components/AiCompareComponent";
import AiIndustryCards from "@components/AiIndustryCards";
import ClientLogos from "@components/new-ui/ClientLogos";
import ProblemSectionCard, { ProblemSection } from "@components/solution/problemSectionCard";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import YellowCTAhref from "@components/yellowCTAhref";

const herosectionData: HerosectionProps = {
    chipTitle: "",
    title: "Gallabox AI bot — Smarter conversations that close more deals",
    description: "Turn WhatsApp into your smartest sales, support, and scheduling assistant.",
    primaryButtonText: "Try for free",
    imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/ai-chatbot-education.png",
};

export const ProblemSectionData: ProblemSection = {
    maintitle: "Benefits of using WhatsApp chatbot platform",
    problemSectionCard: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/blue-clock-icon.svg",
            title: "Scan and qualify leads 24/7",
            description: "Your chatbot screens prospects (“Budget? Timeline?”), routes hot leads to sales, and nurtures curious buyers with tailored content — automatically.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/blue-tickets-icon.svg",
            title: "Resolve routine tickets",
            description: "Bots handle tracking, returns, and FAQs with visual guides. Your team focuses on high-stakes fires like refunds or churn risks.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/blue-file-icon.svg",
            title: "Respond and nurture effortlessly",
            description: "Auto-send rich-media guides (videos/PDFs) the moment prospects ask. No delays, no ghosting — just 90%+ reply rates.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/blue-doc-icon.svg",
            title: "Share catalogs, demos, and guides",
            description: "Zero dead links — just 5X higher engagement by showcasing products via PDFs, send video tutorials, or embed pricing within chats. ",
        },
    ],
};

const featureInfo1: FeatureInfo = {
    mainTitle: "AI lead qualification bot",
    subtitle2: "Never miss a hot lead again. Our AI chatbot asks the right questions, captures details, and qualifies prospects directly – all within WhatsApp.",
    imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/ai-chatbot-lead-qualify.png",
    featureInfoList: ["Answer instantly with automated FAQs.", "Auto-qualify leads for your sales team. ", "Book meetings automatically via calendar sync."],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Book meetings automatically via calendar sync.",
    subtitle2: "Turns FAQs into instant, accurate answers — reducing support load and keeping customers happy.",
    imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/ai-chatbot-support-faq.png",
    featureInfoList: [
        "Upload FAQs for automated, instant replies.",
        "Resolve repetitive queries without human intervention.",
        "Provide 24/7 assistance that customers can rely on.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "AI appointment booking bot (coming soon)",
    subtitle2: "Handles scheduling end-to-end — from showing availability to confirming bookings instantly.",
    imageUrl: "https://assets.gallabox.com/gb-home/ai-chatbot/ai-chatbot-appointment.png",
    featureInfoList: ["Sync calendars for real-time availability.", "Answer appointment queries automatically.", "Confirm bookings instantly without manual work."],
};

const sections = [
    {
        title: "Rewrite for inbox",
        icon: "https://assets.gallabox.com/gb-home/ai-chatbot/message-icon.png",
        features: ["Instantly switch tone: professional, friendly, humorous.", "Fix grammar, shorten/expand text, or simplify sentences in one click."],
        img: "https://assets.gallabox.com/gb-home/ai-chatbot/ai-rewrite.png",
    },
    {
        title: "Template generator",
        icon: "https://assets.gallabox.com/gb-home/ai-chatbot/write-hand.png",
        features: ["Refresh old templates fast.", "Keep campaigns consistent and engaging."],
        img: "https://assets.gallabox.com/gb-home/ai-chatbot/template-generator.png",
    },
    {
        title: "Rule-based bot builder",
        icon: "https://assets.gallabox.com/gb-home/ai-chatbot/robot.png",
        features: ["Describe your workflow in plain English.", "AI builds your bot - no coding required."],
        img: "https://assets.gallabox.com/gb-home/ai-chatbot/rule-based-bot.png",
    },
    {
        title: "Multi-language translate",
        icon: "https://assets.gallabox.com/gb-home/ai-chatbot/world-map.png",
        features: ["Translate messages instantly into multiple languages.", "Break language barriers and connect with customers anywhere."],
        img: "https://assets.gallabox.com/gb-home/ai-chatbot/translation.png",
    },
];

const AiChatbotPage: React.FC = () => {
    return (
        <div className="flex flex-col w-full gap-20 md:gap-24 mb-20">
            <div>
                <Herosection herosection={herosectionData} />

                <div className="lg:max-w-7xl mx-auto">
                    <p className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</p>
                    <ClientLogos />
                </div>
            </div>

            <ProblemSectionCard problemSection={ProblemSectionData} bgColor="var(--Grad-7, linear-gradient(265deg, #396AFF -7.73%, #00238D 61.31%, #000 102.16%))" customStyle />

            <AiCompareComponent />

            <div>
                <h2 className="text-6xl font-semibold  max-w-7xl mx-auto px-5 md:text-center font-degular">Three powerful roles. One smart AI bot.</h2>
                <FeatureInfoCard {...featureInfo1} removeDivider />
                <FeatureInfoCard {...featureInfo2} removeDivider />
                <FeatureInfoCard {...featureInfo3} />
            </div>

            <AiIndustryCards />

            <AIChatbotSection sections={sections} />
            <AiChatbotSectionMobile titleText="Extra AI superpowers inside Gallabox" AiChatbotSectionsPropsData={sections} />
            <YellowCTAhref
                buttonText="Get Started Today"
                customText={
                    <h3 className="font-semibold text-black text-left md:text-center font-degular" style={{ fontSize: "40px" }}>
                        Ready to Bring AI Tools into Your WhatsApp Business Account?
                    </h3>
                }
                cardStyles={{
                    borderRadius: "12px",
                    cardColor: "linear-gradient(129deg, #A4CFFF 1.84%, #E3CEFF 98.21%), linear-gradient(128deg, #F3FFF4 1.19%, #ADE2E8 99.94%)",
                }}
                buttonWithoutIcon
            />
        </div>
    );
};

export default AiChatbotPage;
